import React, { useState, useEffect, useMemo } from 'react';
import useAuthStore from 'Store/useAuthStore';
import {
    getApprovalDiff,
    submitApprovalReview,
    getHistoryList,
    compareCustomVersions
} from 'Api/apiService';
import SmartDiffViewerV3 from './SmartDiffViewerV3';
import { getFullNameFromEmail } from 'Utils/formatting';
import { VIEWMODE } from 'constants/markdown.constants';
import CoreMarkdownEditor from 'Components/SbMarkdownEditor/CoreMarkdownEditor';
import useJobsStore from 'Store/useJobsStore';
import { Popover } from 'antd';
import { SbButton, SbSelect, SbSpin, SbTooltip } from 'uiCore';
import SbInput from 'uiCore/SbInput/SbInput';
import Permission from 'Components/Permission';
import { EditOutlined } from '@ant-design/icons';
import { EditorHeaderStyled, HeaderHeadingStyled, HeaderSubtitleStyled } from 'Components/HumanEditorV2/HumanEditorV2.styles';
import {
    ContainerStyled,
    HeaderStyled,
    ToggleGroupStyled,
    ToggleButtonStyled,
    ViewerAreaStyled,
    HistoryControlsStyled,
    VsLabelStyled,
    SelectWrapperStyled,
    ApproverActionsStyled,
    PendingCountStyled,
    EditorWrapperStyled,
    EditingPanelStyled,
    EmptyStateStyled,
    LoadingOverlayStyled,
    DiffRefetchWrapperStyled,
    DiffRefetchOverlayStyled,
    PopoverContentStyled,
} from './SbDiffCheckerV3.styles';
import SbBadge from 'uiCore/SbBadge/SbBadge';
import SbModal from 'uiCore/SbModal/SbModal';
import { VIEW_MODE, WORKFLOW_STATUS, DECISION, JD_TABS } from 'constants/jobs.constants';

const TOOLTIP_LEFT_VERSION = "Select the version to show on the left (typically the original / base version).";
const TOOLTIP_RIGHT_VERSION = "Select the version to show on the right (typically the proposed / revised version).";
const TOOLTIP_REQUEST_REVISION = "Send the JD back to the creator with your feedback. They will revise the flagged sections and resubmit for approval.";

const BULK_ACTION = Object.freeze({ APPROVED: 'approved', REJECTED: 'rejected' });

const SbDiffCheckerV3 = ({ refreshData, ogJd, fetchOGJobDescription, editableMarkdown, mongoWorkflowStatus, reviewHistory = [] }) => {
    const email = useAuthStore((s) => s.email);
    const selectedJob = useJobsStore((state) => state.selectedJob);
    const setSelectedJdTab = useJobsStore((state) => state.setSelectedJdTab);

    // ── State ──────────────────────────────────────────────────────────────────
    const [loading, setLoading]       = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [diffData, setDiffData]     = useState(null);
    const [decisions, setDecisions]   = useState({});
    const [viewMode, setViewMode]     = useState(VIEW_MODE.LOADING);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [historyOptions, setHistoryOptions]   = useState([]);
    const [selectedLeft, setSelectedLeft]       = useState(null);
    const [selectedRight, setSelectedRight]     = useState(null);
    const [editorValue, setEditorValue] = useState('');
    const [toast, setToast] = useState({ message: '', show: false, type: 'error' });
    const [rejectPopover, setRejectPopover] = useState({ open: false, comment: '' });
    const [bulkActionState, setBulkActionState] = useState(null);
    const [hasInteracted, setHasInteracted] = useState(false);

    // LHS must always be strictly lower than RHS (Dynamic Filtering)
    const compareVersions = (a, b) => {
        const [aMajor, aMinor] = a.split('.').map(Number);
        const [bMajor, bMinor] = b.split('.').map(Number);
        if (aMajor !== bMajor) return aMajor - bMajor;
        return aMinor - bMinor;
    };

    const lhsOptions = useMemo(() => {
        if (historyOptions.length <= 1) return historyOptions;
        if (!hasInteracted) {
            const maxOption = historyOptions.reduce((best, o) =>
                compareVersions(o.value, best.value) > 0 ? o : best
            );
            return historyOptions.filter((o) => o.value !== maxOption.value);
        }
        if (selectedRight == null) return historyOptions;
        return historyOptions.filter((o) => compareVersions(o.value, selectedRight) < 0);
    }, [historyOptions, hasInteracted, selectedRight]);


    const rhsOptions = useMemo(() => {
        if (historyOptions.length <= 1) return historyOptions;
        if (!hasInteracted) {
            const minOption = historyOptions.reduce((best, o) =>
                compareVersions(o.value, best.value) < 0 ? o : best
            );
            return historyOptions.filter((o) => o.value !== minOption.value);
        }
        if (selectedLeft == null) return historyOptions;
        return historyOptions.filter((o) => compareVersions(o.value, selectedLeft) > 0);
    }, [historyOptions, hasInteracted, selectedLeft]);

    const findNearestBelow = (value) => {
        const candidates = historyOptions.filter((o) => compareVersions(o.value, value) < 0);
        if (!candidates.length) return null;
        return candidates.reduce((best, o) =>
            compareVersions(o.value, best.value) > 0 ? o : best
        ).value;
    };

    const findNearestAbove = (value) => {
        const candidates = historyOptions.filter((o) => compareVersions(o.value, value) > 0);
        if (!candidates.length) return null;
        return candidates.reduce((best, o) =>
            compareVersions(o.value, best.value) < 0 ? o : best
        ).value;
    };

    const lastInitializedKey = React.useRef(null);
    const lastInitializedJobRef = React.useRef(null);

    const isApprover = reviewHistory?.[reviewHistory.length - 1]?.assigned_approver_email === email;

    // ── Fetch base JD lazily when there are no diffs ───────────────────────────
    useEffect(() => {
        const isNoDiff =
            diffData?.diff?.right_pane?.length === 0 ||
            (diffData?.diff?.left_pane?.every(i => i.status === 'unchanged') &&
             diffData?.diff?.right_pane?.every(i => i.status === 'unchanged'));
        if (isNoDiff && !ogJd) fetchOGJobDescription();
    }, [diffData, ogJd]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Initialize once per job — re-runs when job/status changes,
    //    OR when the same job is re-selected (new object reference from store) ──
    useEffect(() => {
        if (!selectedJob?.job_id || !mongoWorkflowStatus || mongoWorkflowStatus === WORKFLOW_STATUS.LOADING_STATUS) return;
        const initKey = `${selectedJob.job_id}-${mongoWorkflowStatus}`;
        if (lastInitializedKey.current === initKey && lastInitializedJobRef.current === selectedJob) return;
        lastInitializedKey.current    = initKey;
        lastInitializedJobRef.current = selectedJob;
        initializeView();
    }, [selectedJob, mongoWorkflowStatus]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── 1. Initialize ──────────────────────────────────────────────────────────
    const initializeView = async () => {
        setViewMode(VIEW_MODE.LOADING);
        setLoading(true);
        setDiffData(null);
        setDecisions({});
        setBulkActionState(null);
        try {
            const latestCycle  = reviewHistory.length > 0 ? reviewHistory[reviewHistory.length - 1] : null;
            const isAuthorized = latestCycle?.assigned_approver_email === email;

            if (mongoWorkflowStatus === WORKFLOW_STATUS.PENDING_APPROVAL && isAuthorized) {
                setViewMode(VIEW_MODE.APPROVER);
                await fetchApproverData();
            } else {
                setViewMode(VIEW_MODE.HISTORY);
                await fetchHistoryOptions();
            }
        } catch (error) {
            console.error('Initialization Error:', error);
            setViewMode(VIEW_MODE.HISTORY);
            await fetchHistoryOptions();
        } finally {
            setLoading(false);
        }
    };

    // ── 2. Switch handlers ─────────────────────────────────────────────────────
    const switchToApprover = async () => {
        if (!isApprover) return;
        setLoading(true);
        setDiffData(null);
        setDecisions({});
        setBulkActionState(null);
        setViewMode(VIEW_MODE.APPROVER);
        await fetchApproverData();
        setLoading(false);
    };

    const switchToHistory = async () => {
        setLoading(true);
        setDiffData(null);
        setDecisions({});
        setSelectedLeft(null);
        setSelectedRight(null);
        setViewMode(VIEW_MODE.HISTORY);
        await fetchHistoryOptions();
        setLoading(false);
    };

    // ── 3. Data fetching ───────────────────────────────────────────────────────
    const fetchApproverData = async () => {
        try {
            const data = await getApprovalDiff(selectedJob.job_id);
            setIsReadOnly(false);
            setDiffData(data);
            if (data.comments?.length > 0) {
                const preloaded = {};
                data.comments.forEach((c) => {
                    preloaded[c.element_id] = {
                        element_id: c.element_id,
                        diff_type: c.diff_type,
                        decision: c.decision,
                        comment: c.comment,
                        isHistorical: true,
                    };
                });
                setDecisions(preloaded);
            }
        } catch {
            setToast({
                message: 'Failed to load approval comparison.',
                show: true,
                type: 'error',
            })
        }
    };

    const fetchHistoryOptions = async () => {
        setHasInteracted(false);
        try {
            const list    = await getHistoryList(selectedJob.job_id);
            const sorted  = [...list].sort((a, b) => b.version - a.version);
            const options = sorted.map((v) => ({ value: v.version, label: v.tag ? `V${v.version} (${v.tag})` : `V${v.version}` }));
            setHistoryOptions(options);
            if (options.length >= 2) {
                setSelectedRight(options[0].value);
                setSelectedLeft(options[1].value);
                await fetchCustomCompare(options[1].value, options[0].value);
            } else if (options.length === 1) {
                setSelectedRight(options[0].value);
                setSelectedLeft(options[0].value);
                await fetchCustomCompare(options[0].value, options[0].value);
            }
        } catch (err) {
            console.error('History options error:', err);
        }
    };

    const fetchCustomCompare = async (vLeft, vRight) => {
        if (!vLeft || !vRight) return;
        setLoading(true);
        setDecisions({});
        try {
            const data = await compareCustomVersions(selectedJob.job_id, vLeft, vRight);
            setDiffData(data);
            setIsReadOnly(true);
        } catch {
            setToast({
                message: 'Comparison failed.',
                show: true,
                type: 'error',
            });
            setDiffData(null);
            setSelectedLeft(null);
            setSelectedRight(null);
        } finally {
            setLoading(false);
        }
    };

    // ── 4. Decision logic ──────────────────────────────────────────────────────
    const handleDecisionChange = (elementIds, diffType, decision, comment, text) => {
        const ids = Array.isArray(elementIds) ? elementIds : [elementIds];
        const finalComment = decision === DECISION.APPROVE ? null : comment;
        setDecisions((prev) => {
            const updated = { ...prev };
            ids.forEach((id) => {
                updated[id] = {
                    element_id: id,
                    diff_type: diffType,
                    decision,
                    comment: finalComment,
                    removed_text: diffType === DECISION.REMOVED ? text : null,
                    isHistorical: false,
                };
            });
            return updated;
        });
    };

    const handleClearDecision = (elementIds) => {
        const ids = Array.isArray(elementIds) ? elementIds : [elementIds];
        setDecisions((prev) => {
            const updated = { ...prev };
            ids.forEach((id) => delete updated[id]);
            return updated;
        });
    };

    // Deduped list of all changed items — shared by bulk handlers and pendingCount
    const changedDiffItems = useMemo(() => {
        if (!diffData?.diff) return [];
        const seen  = new Set();
        const items = [];
        diffData.diff.right_pane.forEach((item) => {
            if (item.status !== 'unchanged' && !seen.has(item.element_id)) {
                seen.add(item.element_id);
                items.push(item);
            }
        });
        diffData.diff.left_pane.forEach((item) => {
            if (item.status === 'removed' && !seen.has(item.element_id)) {
                seen.add(item.element_id);
                items.push(item);
            }
        });
        return items;
    }, [diffData]);

    const handleApproveAll = () => {
        changedDiffItems.forEach((item) => {
            const existing    = decisions[item.element_id];
            const canOverride = !existing || existing.isHistorical
                || (bulkActionState === BULK_ACTION.REJECTED && existing.decision === DECISION.REVIEW);
            if (canOverride) {
                handleDecisionChange(item.element_id, item.status.toUpperCase(), DECISION.APPROVE, null, item.text);
            }
        });
        setBulkActionState(BULK_ACTION.APPROVED);
    };

    const handleRejectAll = () => {
        const { comment } = rejectPopover;
        if (!comment.trim()) return;
        changedDiffItems.forEach((item) => {
            const existing    = decisions[item.element_id];
            const canOverride = !existing || existing.isHistorical
                || (bulkActionState === BULK_ACTION.APPROVED && existing.decision === DECISION.APPROVE);
            if (canOverride) {
                handleDecisionChange(item.element_id, item.status.toUpperCase(), DECISION.REVIEW, comment.trim(), item.text);
            }
        });
        setRejectPopover({ open: false, comment: '' });
        setBulkActionState(BULK_ACTION.REJECTED);
    };

    const handleResetAll = () => {
        handleClearDecision(changedDiffItems.map((item) => item.element_id));
        setBulkActionState(null);
    };

    const isAllDecisionsApproved = useMemo(() => (
        Object.values(decisions)
            .filter((d) => !d.isHistorical)
            .every((d) => d.decision === DECISION.APPROVE)
    ), [decisions]);

    const pendingCount = useMemo(() => (
        changedDiffItems.filter(({ element_id }) => {
            const d = decisions[element_id];
            return !d || d.isHistorical;
        }).length
    ), [changedDiffItems, decisions]);

    const enrichedDiffData = useMemo(() => {
        if (!diffData?.diff) return null;
        const enrich = (list) => list.map((item) => ({
            ...item,
            decision: decisions[item.element_id]?.decision || null,
            comment:  decisions[item.element_id]?.comment  || null,
            isHistorical: decisions[item.element_id]?.isHistorical,
            ghostContext: null,
        }));

        const enrichedLeft  = enrich([...(diffData.diff.left_pane  || [])]);
        const enrichedRight = enrich([...(diffData.diff.right_pane || [])]);

        (diffData.comments || [])
            .filter((c) => c.diff_type === DECISION.REMOVED && c.decision === DECISION.REVIEW)
            .forEach((ghost) => {
                const target = enrichedRight.find((r) => r.linked_id === ghost.element_id);
                if (target) {
                    target.ghostContext = { comment: ghost.comment, originalText: ghost.removed_text };
                    const lhs = enrichedLeft.find((l) => l.element_id === ghost.element_id);
                    if (lhs) { lhs.decision = null; lhs.comment = null; }
                }
            });

        return { ...diffData, left_pane: enrichedLeft, right_pane: enrichedRight };
    }, [diffData, decisions]);

    // ── 5. Submit / Publish ────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (!isDiffReady || pendingCount > 0) return;
        setSubmitting(true);
        try {
            const nonHistorical  = Object.values(decisions).filter((d) => !d.isHistorical);
            const decisionsArray = nonHistorical.length > 0
                ? nonHistorical.map(({ element_id, diff_type, decision, comment, removed_text }) => ({
                    element_id, diff_type, decision, comment, removed_text,
                  }))
                : [{ element_id: '', diff_type: DECISION.MODIFIED, decision: DECISION.APPROVE, comment: null, removed_text: null }];
            const response = await submitApprovalReview(selectedJob.job_id, decisionsArray, isEdited ? editorValue : null);
            setToast({
                message: isEdited ? 'Published successfully!' : 'Review submitted successfully!',
                show: true,
                type: 'success',
            });
            if (refreshData) await refreshData(response.new_sb_unique_id);
            setIsEditing(false);
            setSelectedJdTab(JD_TABS.JOB_DESCRIPTION);
        } catch {
            setToast({
                message: 'Submission failed.',
                show: true,
                type: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Seeded with what the editor displays, so opening the editor alone is not an edit.
    const handleEditClick = () => {
        setEditorValue(editableMarkdown || '');
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setEditorValue('');
        setIsEditing(false);
    };

    const normalizeMd = (text) => (typeof text === 'string' ? text : '').replace(/\s+/g, ' ').trim();
    const isEdited = isEditing && normalizeMd(editorValue) !== normalizeMd(editableMarkdown);

    // ── Render ─────────────────────────────────────────────────────────────────
    const currentUserName = getFullNameFromEmail(email) || email;
    // Changes loaded and not being refreshed. Until then there is nothing to have reviewed: with no
    // diff yet, pendingCount is 0 and isAllDecisionsApproved is vacuously true, which would otherwise
    // offer "Approve" and publish the JD without the approver seeing a single change.
    const isDiffReady     = !loading && Boolean(diffData?.diff);
    const canEdit         = isDiffReady && isAllDecisionsApproved && pendingCount === 0;
    const isNoDiff        = diffData?.diff && (
        diffData.diff.right_pane.length === 0 ||
        (diffData.diff.left_pane.every((i) => i.status === 'unchanged') &&
         diffData.diff.right_pane.every((i) => i.status === 'unchanged') &&
         viewMode === VIEW_MODE.APPROVER)
    );
    const isRefetching    = loading && Boolean(diffData?.diff);

    if (viewMode === VIEW_MODE.LOADING) {
        return (
            <ContainerStyled>
                <LoadingOverlayStyled>
                    <SbSpin size="large" />
                </LoadingOverlayStyled>
            </ContainerStyled>
        );
    }

    return (
        <ContainerStyled>

            <SbModal
                open={toast.show}
                title={toast.type === 'error' ? 'Error' : 'Success'}
                onCancel={() => setToast(prev => ({ ...prev, show: false }))}
                wrapClassName="diff-toast-modal"
                footer={[
                    <SbButton
                        key="ok"
                        buttonType="primary"
                        onClick={() => setToast(prev => ({ ...prev, show: false }))}
                    >
                        OK
                    </SbButton>,
                ]}
            >
                {toast.message}
            </SbModal>

            {/* ── Header ── */}
            <HeaderStyled>

                {/* Mode toggle */}
                <ToggleGroupStyled>
                    <ToggleButtonStyled
                        $active={viewMode === VIEW_MODE.APPROVER}
                        onClick={switchToApprover}
                        disabled={mongoWorkflowStatus !== WORKFLOW_STATUS.PENDING_APPROVAL || !isApprover}
                    >
                        Approval Review
                        {mongoWorkflowStatus === WORKFLOW_STATUS.PENDING_APPROVAL && isApprover && (
                            <SbBadge count="!" size="small" />
                        )}
                    </ToggleButtonStyled>
                    <ToggleButtonStyled $active={viewMode === VIEW_MODE.HISTORY} onClick={switchToHistory}>
                        Version Compare
                    </ToggleButtonStyled>
                </ToggleGroupStyled>

                {/* History version selectors */}
                {viewMode === VIEW_MODE.HISTORY && (
                    <HistoryControlsStyled>
                        <SbTooltip content={TOOLTIP_LEFT_VERSION}>
                            <SelectWrapperStyled>
                                <SbSelect
                                    options={lhsOptions}
                                    value={selectedLeft}
                                    placeholder="Base Version"
                                    onChange={(val) => {
                                        setHasInteracted(true);
                                        let nextRight = selectedRight;
                                        if (selectedRight != null && compareVersions(selectedRight, val) <= 0) {
                                            nextRight = findNearestAbove(val) ?? selectedRight;
                                            setSelectedRight(nextRight);
                                        }
                                        setSelectedLeft(val);
                                        fetchCustomCompare(val, nextRight);
                                    }}
                                />
                            </SelectWrapperStyled>
                        </SbTooltip>
                        <VsLabelStyled>VS</VsLabelStyled>
                        <SbTooltip content={TOOLTIP_RIGHT_VERSION}>
                            <SelectWrapperStyled>
                                <SbSelect
                                    options={rhsOptions}
                                    value={selectedRight}
                                    placeholder="Comparison Version"
                                    onChange={(val) => {
                                        setHasInteracted(true);
                                        let nextLeft = selectedLeft;
                                        if (selectedLeft != null && compareVersions(selectedLeft, val) >= 0) {
                                            nextLeft = findNearestBelow(val) ?? selectedLeft;
                                            setSelectedLeft(nextLeft);
                                        }
                                        setSelectedRight(val);
                                        fetchCustomCompare(nextLeft, val);
                                    }}
                                />
                            </SelectWrapperStyled>
                        </SbTooltip>
                    </HistoryControlsStyled>
                )}

                {/* Approver actions */}
                {viewMode === VIEW_MODE.APPROVER && (
                    <ApproverActionsStyled>
                        {!isEditing && (
                            <>
                                <PendingCountStyled $hasRemaining={pendingCount > 0}>
                                    {loading
                                        ? 'Loading changes…'
                                        : !isDiffReady
                                            ? "Couldn't load changes. Refresh to try again."
                                            : pendingCount > 0 ? `${pendingCount} decisions remaining` : 'All changes reviewed'}
                                </PendingCountStyled>

                                {(pendingCount > 0 || bulkActionState === BULK_ACTION.REJECTED) && (
                                    <SbTooltip
                                        title="Approve all remaining decision items"
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    >
                                        <SbButton buttonType="primary" ghost onClick={handleApproveAll}>
                                            Approve All
                                        </SbButton>
                                    </SbTooltip>
                                )}

                                {bulkActionState === BULK_ACTION.APPROVED && (
                                    <SbTooltip
                                        title="Reset all bulk-approved decisions"
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    >
                                        <SbButton buttonType="secondary" onClick={handleResetAll}>
                                            Reset All
                                        </SbButton>
                                    </SbTooltip>
                                )}

                                {(pendingCount > 0 || bulkActionState === BULK_ACTION.APPROVED) && (
                                    <Popover
                                        open={rejectPopover.open}
                                        trigger="click"
                                        onOpenChange={(visible) =>
                                            setRejectPopover((prev) => ({
                                                ...prev,
                                                open:    visible,
                                                comment: visible ? prev.comment : '',
                                            }))
                                        }
                                        placement="bottomRight"
                                        title="Reject All Changes"
                                        content={
                                            <PopoverContentStyled>
                                                <SbInput
                                                    textarea
                                                    rows={3}
                                                    placeholder="Reason for rejection..."
                                                    value={rejectPopover.comment}
                                                    onChange={(e) =>
                                                        setRejectPopover((prev) => ({ ...prev, comment: e.target.value }))
                                                    }
                                                />
                                                <SbButton
                                                    buttonType="red"
                                                    size="small"
                                                    disabled={!rejectPopover.comment.trim()}
                                                    onClick={handleRejectAll}
                                                >
                                                    Reject
                                                </SbButton>
                                            </PopoverContentStyled>
                                        }
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    >
                                        <span>
                                            <SbButton
                                                buttonType="primary"
                                                ghost
                                                danger
                                                onClick={() => setRejectPopover((prev) => ({ ...prev, open: !prev.open }))}
                                            >
                                                Reject All
                                            </SbButton>
                                        </span>
                                    </Popover>
                                )}

                                Assigned to: <strong>{currentUserName}</strong>

                                <Permission resource="jobs_library-job_details" actions={['edit']}>
                                    <SbTooltip
                                        title={canEdit ? 'Edit' : 'Approve all changes before editing'}
                                        getPopupContainer={(trigger) => trigger.parentElement}
                                    >
                                        <span>
                                            <SbButton
                                                buttonType="primary"
                                                icon={<EditOutlined />}
                                                disabled={!canEdit}
                                                onClick={canEdit ? handleEditClick : undefined}
                                            />
                                        </span>
                                    </SbTooltip>
                                </Permission>
                            </>
                        )}
                        <SbTooltip
                            content={TOOLTIP_REQUEST_REVISION}
                            disabled={isEditing || isAllDecisionsApproved}
                            getPopupContainer={(trigger) => trigger.parentElement}
                        >
                            <span>
                                <SbButton
                                    buttonType='primary'
                                    loading={submitting}
                                    disabled={submitting || !isDiffReady || (!isEditing && pendingCount > 0)}
                                    onClick={handleSubmit}
                                >
                                {isEdited ? "Publish" : isAllDecisionsApproved ? "Approve" : "Submit"}
                                </SbButton>
                            </span>
                        </SbTooltip>
                    </ApproverActionsStyled>
                )}
            </HeaderStyled>
            <ViewerAreaStyled>
                {isEditing ? (
                    <EditingPanelStyled>
                        <EditorHeaderStyled>
                            <div>
                                <HeaderHeadingStyled>Editing Draft</HeaderHeadingStyled>
                                <HeaderSubtitleStyled>Prepare your draft and publish.</HeaderSubtitleStyled>
                            </div>
                            <SbButton buttonType="secondary" onClick={handleCancelEdit}>Cancel</SbButton>
                        </EditorHeaderStyled>
                        <EditorWrapperStyled>
                            <CoreMarkdownEditor
                                key={editableMarkdown}
                                initialMarkdown={editableMarkdown || ""}
                                viewMode={VIEWMODE.EDIT}
                                onChange={setEditorValue}
                            />
                        </EditorWrapperStyled>
                    </EditingPanelStyled>
                ) : (
                    <>
                        {diffData?.diff ? (
                            isNoDiff ? (
                                <EditorWrapperStyled>
                                    <div id="content-to-download">
                                        <CoreMarkdownEditor
                                            key={ogJd}
                                            initialMarkdown={ogJd}
                                            viewMode={VIEWMODE.EDIT}
                                            onChange={setEditorValue}
                                        />
                                    </div>
                                </EditorWrapperStyled>
                            ) : (
                                <DiffRefetchWrapperStyled $active={isRefetching}>
                                    <SmartDiffViewerV3
                                        diffData={enrichedDiffData}
                                        onDecisionChange={handleDecisionChange}
                                        onClearDecision={handleClearDecision}
                                        viewMode={viewMode}
                                        readOnly={isReadOnly}
                                    />
                                    {isRefetching && (
                                        <DiffRefetchOverlayStyled>
                                            <SbSpin size="large" />
                                            <span>Loading Diff</span>
                                        </DiffRefetchOverlayStyled>
                                    )}
                                </DiffRefetchWrapperStyled>
                            )
                        ) : (
                            !loading && (
                                <EmptyStateStyled>
                                    Select versions to compare.
                                </EmptyStateStyled>
                            )
                        )}
                    </>
                )}

                {loading && viewMode !== VIEW_MODE.LOADING && !diffData?.diff && (
                    <LoadingOverlayStyled>
                        <SbSpin size="small" /> Loading diff...
                    </LoadingOverlayStyled>
                )}
            </ViewerAreaStyled>
        </ContainerStyled>
    );
};

export default SbDiffCheckerV3;
